import DuplicateAdsetModal from "@components/DuplicateAdsetModal/DuplicateAdsetModal"
import DuplicateCampaignModal from "@components/DuplicateCampaignModal/DuplicateCampaignModal"
import React, { useEffect, useState } from "react"
import { Autocomplete, TextField } from "@mui/material"
// Removed unused imports
import { notify } from "@utils/notify"
import { fetchFreshFacebookCampaigns, fetchFreshFacebookAdsetsByCampaign } from "@services/getAllCampaigns"
import fetchFreshFacebookAdsets from "@services/getAllAdSets.js"

const BulkEditor = ({ onApply, overwriteExisting, setOverwriteExisting, applyDisabled, onSelectionChange }) => {
  const adAccountId = localStorage.getItem("adAccountId")
  const campaignObjective = localStorage.getItem("campaignObjective")
  const userId = localStorage.getItem("userId")

  // state
  const [selectedCampaignId, setSelectedCampaignId] = useState("")
  const [selectedAdSetId, setSelectedAdSetId] = useState("")
  const [selectedAdset, setSelectedAdset] = useState(null)
  const [openCampaignModal, setOpenCampaignModal] = useState(false)
  const [openAdSetModal, setOpenAdSetModal] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [filteredCampaigns, setFilteredCampaigns] = useState([])
  const [selectedAppCampaign, setSelectedAppCampaign] = useState(false)

  // Fresh data states
  const [freshCampaigns, setFreshCampaigns] = useState([])
  const [freshAdSets, setFreshAdSets] = useState([])
  const [adSetsByCampaign, setAdSetsByCampaign] = useState([])
  const [loadingFreshCampaigns, setLoadingFreshCampaigns] = useState(false)
  const [loadingAdSetsByCampaign, setLoadingAdSetsByCampaign] = useState(false)

  // selectors
  // const { campaigns } = useSelector((state) => state.campaigns)
  // const { adSets } = useSelector((state) => state.adSets)

  // hooks
  // const { handleGetCampaigns } = useCampaigns()
  // const { handleGetAdSets } = useAdSets()

  // Fetch fresh campaigns from API
  useEffect(() => {
    async function fetchCampaigns() {
      if (!userId || !adAccountId) return
      setLoadingFreshCampaigns(true)
      try {
        const response = await fetchFreshFacebookCampaigns({
          user_id: userId,
          ad_account_id: adAccountId,
        })
        setFreshCampaigns(response.data || [])
      } catch (err) {
        console.error("Error fetching fresh campaigns:", err)
        setFreshCampaigns([])
      }
      setLoadingFreshCampaigns(false)
    }
    fetchCampaigns()
  }, [userId, adAccountId])

  // Fetch fresh adsets from API
  useEffect(() => {
    async function fetchAdSets() {
      if (!userId || !adAccountId) return
      try {
        const response = await fetchFreshFacebookAdsets({
          user_id: userId,
          ad_account_id: adAccountId,
        })
        if (response?.data) {
          setFreshAdSets(response.data)
        } else {
          setFreshAdSets([])
        }
      } catch (err) {
        console.log(err)
        setFreshAdSets([])
      }
    }
    fetchAdSets()
  }, [userId, adAccountId])

  // effects
  // useEffect(() => {
  //   async function fetchData() {
  //     if (adAccountId || openCampaignModal === false) {
  //       await handleGetCampaigns(adAccountId)
  //     }
  //   }
  //   fetchData()
  // }, [adAccountId, openCampaignModal])


  // Filter fresh campaigns based on objective
  useEffect(() => {
    if (freshCampaigns.length > 0) {
      let data = freshCampaigns
      if (campaignObjective === 'OUTCOME_APP_PROMOTION') {
        setSelectedAppCampaign(true)
        data = data.filter(campaign => campaign.objective === campaignObjective)
      } else {
        data = data.filter(campaign => campaign.objective !== "OUTCOME_APP_PROMOTION")
      }
      setFilteredCampaigns(data)
    }
  }, [freshCampaigns, campaignObjective])

  // useEffect(() => {
  //   if (selectedCampaignId || openAdSetModal === false) handleGetAdSets(selectedCampaignId)
  // }, [selectedCampaignId, openAdSetModal])

  // Handle campaign change and fetch adsets for selected campaign
  const handleCampaignChange = async (_, newValue) => {
    const campaignId = newValue?.id || newValue?.campaignId || ""
    setSelectedCampaignId(campaignId)
    setSelectedCampaign(newValue)
    // Reset adset when campaign changes
    setSelectedAdSetId("")
    setSelectedAdset(null)
    onSelectionChange?.(campaignId, newValue, "", null)

    if (campaignId && userId) {
      // Fetch adsets for selected campaign
      setLoadingAdSetsByCampaign(true)
      try {
        const response = await fetchFreshFacebookAdsetsByCampaign({
          user_id: userId,
          campaign_id: campaignId,
          ad_account_id: adAccountId,
          fetch_ads: true
        })
        setAdSetsByCampaign(response?.data || [])
      } catch (err) {
        console.log(err)
        setAdSetsByCampaign([])
      }
      setLoadingAdSetsByCampaign(false)
    } else {
      setAdSetsByCampaign([])
    }
  }

  const handleAdSetChange = (_, newValue) => {
    const adsetId = newValue?.id || newValue?.adsetId || ""
    setSelectedAdSetId(adsetId)
    setSelectedAdset(newValue)
    onSelectionChange?.(selectedCampaignId, selectedCampaign, adsetId, newValue)

    if (newValue?.adsCount > 45) {
      notify(`Warning: Adset already contains ${newValue.ads.data.length} ads out of 50 allowed. Consider duplicating the adset.`, "warning")
    }
  }

  const handleApply = () => {
    onApply(
      selectedAdSetId,
      selectedAdset,
      selectedCampaignId,
      selectedCampaign
    )
  }

  // Refresh functions for modals
  const refreshCampaigns = async () => {
    setLoadingFreshCampaigns(true)
    try {
      const response = await fetchFreshFacebookCampaigns({
        user_id: userId,
        ad_account_id: adAccountId,
      })
      setFreshCampaigns(response.data || [])
    } catch (err) {
      console.log(err)
      setFreshCampaigns([])
    }
    setLoadingFreshCampaigns(false)
  }

  const refreshBothCampaignsAndAdsets = async () => {
    console.log("Refreshing adsets and campaigns...")
    if (!adAccountId || !userId) return;
    setLoadingAdSetsByCampaign(true);
    try {
      // 1. Refresh campaigns
      const campaignsRes = await fetchFreshFacebookCampaigns({
        user_id: userId,
        ad_account_id: adAccountId,
        force_refresh: true,
      });
      setFreshCampaigns(campaignsRes?.data || []);

      // 2. Refresh all ad sets for the ad account
      const allAdsetsRes = await fetchFreshFacebookAdsets({
        user_id: userId,
        ad_account_id: adAccountId,
        force_refresh: true,
      });
      setFreshAdSets(allAdsetsRes?.data || []);

      // 3. Always refresh ad sets for the selected campaign (even if falsy)
      let campaignIdToUse = selectedCampaignId;
      // If not set, try to use the first campaign from freshCampaigns
      if (!campaignIdToUse && campaignsRes?.data?.length > 0) {
        campaignIdToUse = campaignsRes.data[0].id || campaignsRes.data[0].campaignId;
        setSelectedCampaignId(campaignIdToUse);
      }
      if (campaignIdToUse) {
        const adsetsByCampaignRes = await fetchFreshFacebookAdsetsByCampaign({
          user_id: userId,
          campaign_id: campaignIdToUse,
          ad_account_id: adAccountId,
          fetch_ads: true,
        });
        setAdSetsByCampaign(adsetsByCampaignRes?.data || []);
      } else {
        setAdSetsByCampaign([]);
      }
    } catch (err) {
      console.error("Error refreshing campaigns and ad sets:", err);
      setFreshCampaigns([]);
      setFreshAdSets([]);
      setAdSetsByCampaign([]);
    } finally {
      setLoadingAdSetsByCampaign(false);
    }
  }

  const refreshAdsets = async () => {
    // console.log("Refreshing adsets only...")
    if (!adAccountId || !userId) return;
    setLoadingAdSetsByCampaign(true);
    try {
      // 2. Refresh all ad sets for the ad account
      const allAdsetsRes = await fetchFreshFacebookAdsets({
        user_id: userId,
        ad_account_id: adAccountId,
        force_refresh: false,
      });
      setFreshAdSets(allAdsetsRes?.data || []);

      // 3. Always refresh ad sets for the selected campaign (even if falsy)
      let campaignIdToUse = selectedCampaignId;
      // If not set, try to use the first campaign from freshCampaigns
      if (!campaignIdToUse && campaignsRes?.data?.length > 0) {
        campaignIdToUse = campaignsRes.data[0].id || campaignsRes.data[0].campaignId;
        setSelectedCampaignId(campaignIdToUse);
      }
      if (campaignIdToUse) {
        const adsetsByCampaignRes = await fetchFreshFacebookAdsetsByCampaign({
          user_id: userId,
          campaign_id: campaignIdToUse,
          ad_account_id: adAccountId,
          fetch_ads: true
        });
        setAdSetsByCampaign(adsetsByCampaignRes?.data || []);
      } else {
        setAdSetsByCampaign([]);
      }
    } catch (err) {
      console.error("Error refreshing ad sets:", err);
      setFreshCampaigns([]);
      setFreshAdSets([]);
      setAdSetsByCampaign([]);
    } finally {
      setLoadingAdSetsByCampaign(false);
    }
  }

  return (
    <div className="sticky z-20 p-6 mb-6 transition-all duration-200 border rounded-lg bg-gray-50 top-0 shadow-sm hover:shadow-md hover:shadow-gray-200">
      <h2 className="mb-4 text-xl font-semibold text-gray-800">Bulk Editor</h2>

      {/* duplicate campaign and adset */}
      <div className="flex gap-4 mb-4">
        <button
          className="px-4 py-2 text-gray-800 transition-colors duration-200 border border-gray-300 rounded-md hover:bg-gray-50"
          onClick={() => setOpenCampaignModal(true)}
        >
          Duplicate Campaign
        </button>
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 text-gray-800 transition-colors duration-200 border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={() => selectedCampaignId && setOpenAdSetModal(true)}
            disabled={!selectedCampaignId}
          >
            Duplicate Ad Set
          </button>
          <button
            type="button"
            className={`flex items-center justify-center w-8 h-8 transition-colors bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-100 ${loadingAdSetsByCampaign ? 'cursor-wait' : ''}`}
            title="Pull latest Adsets/Campaigns from Meta"
            onClick={loadingAdSetsByCampaign ? undefined : refreshBothCampaignsAndAdsets}
            disabled={loadingAdSetsByCampaign}
            style={{ marginLeft: '4px' }}
          >
            <img
              src="/refresh-gray.svg"
              alt="Refresh"
              className={`w-5 h-5 ${loadingAdSetsByCampaign ? 'animate-spin' : ''}`}
              style={{ transition: 'transform 0.2s' }}
            />
          </button>
        </div>
      </div>

      {/* bulk editor */}
      <div className="grid grid-cols-12 gap-6 mt-6">
        {/* campaigns */}
        <div className="col-span-12 md:col-span-5">
          <Autocomplete
            value={
              filteredCampaigns?.find((camp) => camp.id === selectedCampaignId || camp.campaignId === selectedCampaignId) ||
              null
            }
            onChange={handleCampaignChange}
            options={filteredCampaigns || []}
            getOptionLabel={(option) => option.name || ""}
            loading={loadingFreshCampaigns}
            renderInput={(params) => (
              <TextField {...params} label="Campaign" size="small" />
            )}
          />
        </div>

        {/* ad sets */}
        <div className="col-span-12 md:col-span-5">
          <Autocomplete
            value={adSetsByCampaign?.find((adSet) => adSet.id === selectedAdSetId || adSet.adsetId === selectedAdSetId) || null}
            onChange={handleAdSetChange}
            options={adSetsByCampaign || []}
            loading={loadingAdSetsByCampaign}
            getOptionLabel={(option) => `${option.name} (${option?.adsCount || 0} ads)`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ad Set"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  style: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.id || option.adsetId || option.name}
                style={{
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  width: '100%',
                  display: 'block',
                }}
              >
                {`${option.name} (${option?.adsCount || 0} ads)`}
              </li>
            )}
            style={{
              width: '100%',
            }}
          />

        </div>

        <div className="flex items-end col-span-12 md:col-span-2">
          <button
            type="button"
            onClick={handleApply}
            disabled={applyDisabled || (!selectedCampaignId || !selectedAdSetId)}
            className={`w-full py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white 
              ${applyDisabled || (!selectedCampaignId || !selectedAdSetId)
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              } 
              transition-colors duration-200`}
          >
            Apply
          </button>
        </div>
        <div className="flex col-span-12 md:col-span-5">
          <div className="flex items-center text-sm text-gray-600">
            {selectedAppCampaign ? <span className="font-bold">Showing only App-Install Campaigns</span> : <span className="font-bold">Showing all Non App-Install Campaigns</span>}
          </div>
        </div>
        <div className="flex col-span-12 md:col-span-5">
          <input
            id="overwrite"
            name="overwrite"
            type="checkbox"
            checked={overwriteExisting}
            onChange={(e) => setOverwriteExisting(e.target.checked)}
            className="w-4 h-4 text-blue-600 transition-colors duration-200 border-gray-300 rounded focus:ring-blue-500 hover:ring-2 hover:ring-blue-500"
          />
          <label
            htmlFor="overwrite"
            className="block mb-2 ml-2 text-sm text-gray-700 transition-colors duration-200 hover:text-gray-900"
          >
            Overwrite Existing Data
          </label>
        </div>
      </div>

      {/* Campaign Modal */}
      <DuplicateCampaignModal
        open={openCampaignModal}
        openCampaignModal={openCampaignModal}
        setOpenCampaignModal={setOpenCampaignModal}
        adAccountId={adAccountId}
        adsets={freshAdSets}
        campaigns={freshCampaigns}
        loadingCampaigns={loadingFreshCampaigns}
        refreshCampaigns={refreshCampaigns}
        onClose={(result, msg) => {
          setOpenCampaignModal(false);
          if (result === "success") {
            notify(msg, "success");
          } else if (result === "error") {
            notify(msg, "error");
          }
        }}
      />

      {/* Ad Set Modal */}
      <DuplicateAdsetModal
        open={openAdSetModal}
        openAdSetModal={openAdSetModal}
        setOpenAdSetModal={setOpenAdSetModal}
        adAccountId={adAccountId}
        adsets={freshAdSets}
        campaignAdsets={adSetsByCampaign}
        loadingAdsets={false}
        refreshAdsets={refreshAdsets}
        adsetsByCampaign={adSetsByCampaign}
        selectedCampaign={filteredCampaigns.find(c => c.id === selectedCampaignId || c.campaignId === selectedCampaignId) || null}
        // onClose={(result) => setOpenAdSetModal(false)}
        onClose={(result) => {
          setOpenAdSetModal(false);
          if (result === "success") {
            notify("Ad Set duplicated successfully!", "success");
          } else if (result === "error") {
            notify("Failed to duplicate ad set.", "error");
          }
        }}
      />
    </div>
  )
}

export default BulkEditor